import { useState } from "react";
import { Link } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import Breadcrumb from "@/components/base/Breadcrumb";
import Toast from "@/components/base/Toast";
import usePageMeta from "@/hooks/usePageMeta";

const FORM_URL = "https://readdy.ai/api/form/db9l8m46oi3g3tv8v0eg";

const contacts = [
  { icon: "ri-mail-line", label: "合作邮箱", value: "hello@kanpian-free.example", note: "示意地址，正式上线前可替换" },
  { icon: "ri-wechat-line", label: "微信公众号", value: "看片免费app网站", note: "示意名称，搜索公众号可关注更新" },
  { icon: "ri-time-line", label: "内容更新", value: "每日 10:00 - 22:00", note: "节假日不定期更新影视资料" },
];

const services = [
  {
    icon: "ri-film-line",
    title: "影视资料整理",
    desc: "电影、电视剧、综艺的元信息、剧情简介与评分集中呈现，分类与筛选清晰。",
  },
  {
    icon: "ri-app-store-line",
    title: "看片工具推荐",
    desc: "按收费方式与支持平台整理看片应用，附功能说明与版本更新记录。",
  },
  {
    icon: "ri-search-eye-line",
    title: "快速查找",
    desc: "支持片名、演员、导演与类型关键词搜索，搜索结果可直接进入详情页。",
  },
];

export default function About() {
  const [toast, setToast] = useState("");
  const [status, setStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const [formError, setFormError] = useState("");

  usePageMeta({
    title: "关于我们 - 看片免费app网站",
    description:
      "了解看片免费app网站的定位、内容服务范围与联系方式。我们整理高清影视资料与看片应用推荐，不提供片源存储。",
    keywords: "关于看片免费app网站,影视资源平台,看片app推荐,联系我们,网站反馈",
  });

  const copyText = async (text: string, label: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setToast(`${label}已复制：${text}`);
    } catch {
      setToast("复制失败，请手动选择文本复制。");
    }
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    const honeypot = String(formData.get("website_alt") ?? "").trim();
    if (honeypot) {
      setStatus("success");
      setFormError("");
      form.reset();
      return;
    }

    const payload = new URLSearchParams();
    formData.forEach((value, key) => {
      if (typeof value === "string" && value.trim() !== "") {
        payload.append(key, value);
      }
    });

    setStatus("submitting");
    setFormError("");

    try {
      const response = await fetch(FORM_URL, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload.toString(),
      });
      const responseText = await response.text();
      let parsed: { code?: string; message?: string; meta?: { message?: string; detail?: string } } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMsg =
        parsed?.meta?.message || parsed?.meta?.detail || parsed?.message || responseText;
      const isSpam = typeof serverMsg === "string" && serverMsg.toLowerCase().includes("spam");

      if (!response.ok || parsed?.code !== "OK" || isSpam) {
        setStatus("error");
        setFormError(serverMsg || "提交没有成功，请稍后重试或直接发送邮件联系我们。");
        return;
      }

      setStatus("success");
      setFormError("");
      form.reset();
    } catch {
      setStatus("error");
      setFormError("网络看起来不太稳定，提交失败了，请稍后再试一次。");
    }
  };

  return (
    <SiteLayout>
      <div className="w-full bg-primary-950">
        <div className="w-full max-w-page mx-auto px-4 md:px-8 py-8 md:py-11">
          <Breadcrumb dark items={[{ label: "首页", to: "/" }, { label: "关于我们" }]} />
          <div className="mt-5 max-w-3xl">
            <span className="text-[11px] tracking-[0.22em] text-accent-400 font-label uppercase">About us</span>
            <h1 className="mt-2 text-2xl md:text-3xl font-bold text-background-50">
              关于看片免费app网站
            </h1>
            <p className="mt-3 text-sm md:text-base leading-7 text-primary-200">
              我们是一个面向普通影迷的影视内容门户：整理电影、电视剧、综艺的资料与评分，同时推荐适配的看片应用。
              不做片源存储，不提供盗版资源，只把「看什么、用什么看」这件事说清楚。
            </p>
          </div>
        </div>
      </div>

      <div className="w-full max-w-page mx-auto px-4 md:px-8 mt-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-8">
          <div className="lg:col-span-7 space-y-6">
            <section className="rounded-lg border border-background-200/80 bg-background-50 p-5 md:p-6">
              <h2 className="text-lg font-bold text-foreground-900">网站定位</h2>
              <p className="mt-3 text-sm leading-7 text-foreground-700">
                看片免费app网站聚焦于影视内容整理与看片工具推荐。影视条目包含类型、地区、年份、导演、主演与剧情简介，
                详情页同时提供同类型推荐与相关应用，帮助你从「想看」顺利走到「找到」。
              </p>
              <p className="mt-3 text-sm leading-7 text-foreground-700">
                站点目前处于内容演示阶段：搜索、筛选、分页均可正常使用，下载与播放入口为前端演示，
                待接入正式数据后可直接替换为官方渠道链接。
              </p>
              <div className="mt-5 grid grid-cols-2 sm:grid-cols-4 gap-3">
                {[
                  { value: "38", label: "影视条目" },
                  { value: "8", label: "应用收录" },
                  { value: "20", label: "每页条数" },
                  { value: "7", label: "页面栏目" },
                ].map((stat) => (
                  <div key={stat.label} className="rounded-md bg-background-100 px-3 py-2.5">
                    <p className="text-lg font-bold text-primary-600 font-label">{stat.value}</p>
                    <p className="text-[11px] text-foreground-500 mt-0.5">{stat.label}</p>
                  </div>
                ))}
              </div>
            </section>

            <section className="rounded-lg border border-background-200/80 bg-background-50 p-5 md:p-6">
              <h2 className="text-lg font-bold text-foreground-900">内容服务范围</h2>
              <div className="mt-4 space-y-4">
                {services.map((service) => (
                  <div key={service.title} className="flex gap-3">
                    <span className="w-9 h-9 flex items-center justify-center rounded-md bg-accent-100 text-accent-700 shrink-0">
                      <i className={`${service.icon} text-lg`}></i>
                    </span>
                    <div>
                      <h3 className="text-sm font-semibold text-foreground-900">{service.title}</h3>
                      <p className="mt-1 text-xs leading-6 text-foreground-600">{service.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            <section className="rounded-lg border border-background-200/80 bg-background-50 p-5 md:p-6">
              <h2 className="text-lg font-bold text-foreground-900">联系我们</h2>
              <p className="mt-1.5 text-xs text-foreground-500">点击任意联系方式即可复制。</p>
              <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-3">
                {contacts.map((contact) => (
                  <button
                    key={contact.label}
                    type="button"
                    onClick={() => copyText(contact.value, contact.label)}
                    className="text-left rounded-md border border-background-200 bg-background-50 p-4 hover:border-accent-300 transition-colors cursor-pointer"
                  >
                    <span className="w-8 h-8 flex items-center justify-center rounded-md bg-primary-50 text-primary-500">
                      <i className={`${contact.icon} text-base`}></i>
                    </span>
                    <p className="mt-2.5 text-[11px] text-foreground-500">{contact.label}</p>
                    <p className="mt-0.5 text-sm text-foreground-900 break-all">{contact.value}</p>
                    <p className="mt-1.5 text-[11px] text-foreground-400 leading-5">{contact.note}</p>
                  </button>
                ))}
              </div>
              <div className="mt-4 rounded-md bg-secondary-50 border border-secondary-200 px-4 py-3">
                <p className="text-xs leading-6 text-secondary-900">
                  内容纠错、应用下架或合作事宜，也可以通过下方反馈表单提交，我们会在 1-2 个工作日内查看。
                </p>
              </div>
            </section>

            <section className="rounded-lg border border-background-200/80 bg-background-50 p-5 md:p-6">
              <h2 className="text-lg font-bold text-foreground-900">反馈与建议</h2>
              <p className="mt-1.5 text-xs text-foreground-500">
                表单提交后会直接进入我们的反馈列表，请留下可以联系到你的邮箱。
              </p>

              <form
                id="site-feedback-form"
                data-readdy-form="true"
                onSubmit={handleSubmit}
                className="mt-5 space-y-4"
              >
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="feedback-nickname" className="block text-xs text-foreground-600 mb-1.5">
                      你的称呼
                    </label>
                    <input
                      id="feedback-nickname"
                      name="nickname"
                      type="text"
                      required
                      maxLength={30}
                      placeholder="例如：影迷小李"
                      className="w-full rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 placeholder:text-foreground-400 outline-none focus:border-primary-400"
                    />
                  </div>
                  <div>
                    <label htmlFor="feedback-email" className="block text-xs text-foreground-600 mb-1.5">
                      联系邮箱
                    </label>
                    <input
                      id="feedback-email"
                      name="email"
                      type="email"
                      required
                      maxLength={80}
                      placeholder="you@example.com"
                      className="w-full rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 placeholder:text-foreground-400 outline-none focus:border-primary-400"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="feedback-type" className="block text-xs text-foreground-600 mb-1.5">
                    反馈类型
                  </label>
                  <select
                    id="feedback-type"
                    name="feedback_type"
                    defaultValue="内容纠错"
                    className="w-full rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 outline-none focus:border-primary-400 cursor-pointer"
                  >
                    <option value="内容纠错">内容纠错</option>
                    <option value="应用推荐">应用推荐</option>
                    <option value="功能建议">功能建议</option>
                    <option value="合作咨询">合作咨询</option>
                  </select>
                </div>

                <div>
                  <label htmlFor="feedback-message" className="block text-xs text-foreground-600 mb-1.5">
                    反馈内容
                  </label>
                  <textarea
                    id="feedback-message"
                    name="message"
                    required
                    maxLength={500}
                    rows={4}
                    placeholder="请描述你遇到的问题或建议，最多 500 字"
                    className="w-full rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 placeholder:text-foreground-400 outline-none focus:border-primary-400 resize-y"
                  ></textarea>
                </div>

                <div className="alt-contact-field" aria-hidden="true">
                  <label htmlFor="feedback-website-alt">Company website</label>
                  <input
                    id="feedback-website-alt"
                    name="website_alt"
                    type="text"
                    tabIndex={-1}
                    autoComplete="off"
                    readOnly
                  />
                </div>

                {status === "error" && (
                  <p className="text-xs text-accent-800 bg-accent-50 border border-accent-200 rounded-md px-3 py-2.5 leading-6">
                    {formError}
                  </p>
                )}
                {status === "success" && (
                  <p className="text-xs text-primary-800 bg-primary-50 border border-primary-200 rounded-md px-3 py-2.5 leading-6 flex items-center gap-2">
                    <i className="ri-checkbox-circle-fill text-accent-600"></i>
                    反馈已提交，感谢你的建议，我们会尽快查看。
                  </p>
                )}

                <div className="flex flex-wrap items-center gap-3">
                  <button
                    type="submit"
                    disabled={status === "submitting"}
                    className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm whitespace-nowrap hover:bg-primary-600 transition-colors disabled:opacity-60 disabled:cursor-not-allowed cursor-pointer"
                  >
                    <i className="ri-send-plane-line"></i>
                    {status === "submitting" ? "提交中..." : "提交反馈"}
                  </button>
                  <span className="text-[11px] text-foreground-400">提交后仅用于我们查看反馈，不会公开。</span>
                </div>
              </form>
            </section>
          </div>

          <aside className="lg:col-span-5 space-y-4">
            <div className="rounded-lg bg-primary-950 p-5 md:p-6">
              <h2 className="text-base font-semibold text-background-50">内容声明</h2>
              <ul className="mt-4 space-y-3 text-xs leading-6 text-primary-200">
                {[
                  "本站不存储、不制作任何视频文件，仅展示影视资料信息。",
                  "影视封面与海报为示意素材，实际内容以官方发布为准。",
                  "看片应用下载链接为演示状态，正式渠道接入后自动生效。",
                  "如内容涉及版权问题，可通过邮箱联系我们处理。",
                ].map((item) => (
                  <li key={item} className="flex gap-2">
                    <i className="ri-shield-check-line text-accent-400 mt-0.5"></i>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="rounded-lg border border-background-200/80 bg-background-50 p-5 md:p-6">
              <h2 className="text-base font-semibold text-foreground-900">快速入口</h2>
              <div className="mt-3 grid grid-cols-2 gap-2.5">
                {[
                  { label: "电影频道", to: "/category/电影", icon: "ri-film-line" },
                  { label: "电视剧频道", to: "/category/电视剧", icon: "ri-tv-2-line" },
                  { label: "综艺频道", to: "/category/综艺", icon: "ri-mic-line" },
                  { label: "APP 推荐", to: "/app", icon: "ri-app-store-line" },
                ].map((item) => (
                  <Link
                    key={item.to}
                    to={item.to}
                    className="flex items-center gap-2 rounded-md border border-background-200 px-3 py-2.5 text-xs text-foreground-700 hover:border-accent-300 hover:text-accent-700 transition-colors"
                  >
                    <i className={`${item.icon} text-base`}></i>
                    {item.label}
                  </Link>
                ))}
              </div>
            </div>

            <div className="rounded-lg border border-accent-200 bg-accent-50 p-5">
              <h2 className="text-sm font-semibold text-accent-950">找不到想看的内容？</h2>
              <p className="mt-1.5 text-xs leading-6 text-accent-900">
                可以直接用站内搜索试试别名或演员名，也可以告诉我们你希望补充的影视条目。
              </p>
              <Link
                to="/search"
                className="mt-3 inline-flex items-center gap-1.5 px-4 py-2 rounded-md bg-accent-500 text-primary-950 text-xs whitespace-nowrap hover:bg-accent-400 transition-colors"
              >
                <i className="ri-search-line"></i>
                去搜索
              </Link>
            </div>
          </aside>
        </div>
      </div>

      {toast && <Toast message={toast} onClose={() => setToast("")} />}
    </SiteLayout>
  );
}